/**
 * Device Health time formatting. Everything the health UI shows goes through
 * here so no raw ISO string ever reaches the screen and every time is in ONE
 * timezone (the device's, or the viewer's when the payload carries none).
 */
export function resolveTz(tz) {
  if (!tz || typeof tz !== 'string') return undefined;
  try { new Intl.DateTimeFormat('en-GB', { timeZone: tz }); return tz; } catch { return undefined; }
}

const toMs = (ts) => {
  if (!ts) return null;
  const t = typeof ts === 'number' ? ts : Date.parse(ts);
  return Number.isNaN(t) ? null : t;
};

function parts(ms, tz, opts) {
  const out = {};
  new Intl.DateTimeFormat('en-US', { timeZone: resolveTz(tz), hourCycle: 'h23', ...opts })
    .formatToParts(new Date(ms)).forEach((p) => { out[p.type] = p.value; });
  return out;
}
const ymd = (ms, tz) => { const p = parts(ms, tz, { year: 'numeric', month: '2-digit', day: '2-digit' }); return `${p.year}-${p.month}-${p.day}`; };
const hm = (ms, tz) => { const p = parts(ms, tz, { hour: '2-digit', minute: '2-digit' }); return `${p.hour}:${p.minute}`; };
const md = (ms, tz) => { const p = parts(ms, tz, { month: 'short', day: 'numeric' }); return `${p.month} ${p.day}`; };

/** '9s ago' when it is (almost) now, '02:41' today, 'Oct 8, 23:58' otherwise. '' when unparseable. */
export function formatHealthTime(ts, tz, now = Date.now()) {
  const ms = toMs(ts);
  if (ms === null) return '';
  const diff = Math.floor((now - ms) / 1000);
  if (diff >= 0 && diff < 60) return `${diff}s ago`;
  if (ymd(ms, tz) === ymd(now, tz)) return hm(ms, tz);
  return `${md(ms, tz)}, ${hm(ms, tz)}`;
}

/** Tooltip form: always date + seconds, e.g. 'Oct 8, 02:41:05'. */
export function formatHealthFull(ts, tz) {
  const ms = toMs(ts);
  if (ms === null) return '';
  const p = parts(ms, tz, { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  return `${md(ms, tz)}, ${p.hour}:${p.minute}:${p.second}`;
}

/** Calendar date key (YYYY-MM-DD) in tz — for "is this today" decisions. */
export const healthDayKey = (ts, tz) => { const ms = toMs(ts); return ms === null ? null : ymd(ms, tz); };

/** 'Asia/Riyadh (UTC+3)' style label for the section header. */
export function tzHeaderLabel(tz, now = Date.now()) {
  const z = resolveTz(tz);
  const name = z ?? Intl.DateTimeFormat().resolvedOptions().timeZone ?? 'local time';
  let off = '';
  try {
    const p = new Intl.DateTimeFormat('en-US', { timeZone: z, timeZoneName: 'shortOffset' }).formatToParts(new Date(now)).find((x) => x.type === 'timeZoneName');
    if (p) off = ` (${p.value.replace('GMT', 'UTC')})`;
  } catch { /* offset is cosmetic */ }
  return `${name}${off}`;
}

/** Weekday initial for a YYYY-MM-DD day key (calendar date, tz independent). */
export function weekdayInitial(dateStr) {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(dateStr || '');
  if (!m) return '?';
  return 'SMTWTFS'[new Date(Date.UTC(+m[1], +m[2] - 1, +m[3])).getUTCDay()];
}
export function dayFullLabel(dateStr) {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(dateStr || '');
  if (!m) return String(dateStr ?? '?');
  return new Intl.DateTimeFormat('en-US', { timeZone: 'UTC', weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })
    .format(new Date(Date.UTC(+m[1], +m[2] - 1, +m[3])));
}
